// 아바타 외형 1차(레인 E, docs/AVATAR-ADVENTURE-PRD.md 1.1): 실루엣을 바꾸지 않는 부착물·색만 쓴다.
// 머리 고리(Head 본) · 몸 염색(재질 color에 약하게 곱함) · 망토(Spine2 본, 휜 천 + 셰이더 펄럭임) · 칼날 빛(KaelModel uniform, 카엘만) · 발밑 오라(바닥 고리 2겹, 가산 혼합).
// 장착 상태(AvatarView.revision)·숨기기·영웅·모델·몸 메시가 바뀔 때만 다시 붙인다. 프레임마다 하는 일은 오라 위치·회전과 참조 비교뿐(할당 없음).
import * as THREE from 'three/webgpu';
import { AVATAR_SLOTS, avatarDef, type AvatarView } from '../shared/avatar';
import { Fn, atan, float, frontFacing, fract, abs, length, max, min, mix, positionLocal, select, sin, smoothstep, step, uniform, uv, vec2, vec3 } from 'three/tsl';
import type { KaelModel } from './kael';
import { DYE_STRENGTH_FALLBACK, DYE_STRENGTH_MASKED, applyDyeMasked, clearDye, dyeMaskId, loadDyeMask } from './dyeMask';
import { CAPE_H, CAPE_W_BOTTOM, CAPE_W_TOP, CAPE_COLS, buildCapeGeometry, capeShape } from './capeGeo';
import { CapeCloth, type ClothBodyBones } from './capeCloth';

/** GLTFLoader가 노드 이름의 ':'를 지운다(PropertyBinding.sanitizeNodeName) — 두 이름 다 찾는다 */
const HEAD_BONE = ['mixamorigHead', 'mixamorig:Head'];
const CAPE_BONE = ['mixamorigSpine2', 'mixamorig:Spine2'];
/** 망토 천 몸 충돌용 본(L1030). 못 찾은 본의 캡슐은 생략된다 */
const HIPS_BONE = ['mixamorigHips', 'mixamorig:Hips'];
const LEG_BONES = {
  upLegL: ['mixamorigLeftUpLeg', 'mixamorig:LeftUpLeg'], legL: ['mixamorigLeftLeg', 'mixamorig:LeftLeg'], footL: ['mixamorigLeftFoot', 'mixamorig:LeftFoot'],
  upLegR: ['mixamorigRightUpLeg', 'mixamorig:RightUpLeg'], legR: ['mixamorigRightLeg', 'mixamorig:RightLeg'], footR: ['mixamorigRightFoot', 'mixamorig:RightFoot'],
} as const;
/** 망토 부착 배율(attach의 모델 배율). 천 시뮬의 몸 캡슐 반지름 배율이기도 하다 */
const CAPE_MODEL_SCALE = 1.5;
function findBone(skel: THREE.Skeleton | undefined, names: readonly string[]): THREE.Bone | undefined {
  if (!skel) return undefined;
  for (const n of names) { const b = skel.getBoneByName(n); if (b) return b; }
  return undefined;
}
const SLOT_INDEX = { head: AVATAR_SLOTS.indexOf('head'), dye: AVATAR_SLOTS.indexOf('dye'), cape: AVATAR_SLOTS.indexOf('cape'), weapon: AVATAR_SLOTS.indexOf('weapon'), aura: AVATAR_SLOTS.indexOf('aura') };

type V3 = readonly [number, number, number];
function rgb(hex: number, mul = 1): V3 { return [((hex >> 16) & 255) / 255 * mul, ((hex >> 8) & 255) / 255 * mul, (hex & 255) / 255 * mul]; }

export class AvatarLook {
  /** 장면에 붙는 오라(영웅 루트 회전·점프를 따르지 않게 따로 둔다) */
  readonly aura = new THREE.Group();
  private readonly headRing: THREE.Mesh;
  private readonly cape: THREE.Group;
  private readonly capeMat: THREE.MeshToonNodeMaterial;
  private readonly capeMain = uniform(new THREE.Color(0x6a4e34));
  private readonly capeAccent = uniform(new THREE.Color(0xc89a52));
  /** 가장자리 트림 폭(m) — 레어 망토는 더 굵게 */
  private readonly capeTrimW = uniform(0.045);
  /** 레어 문양 띠 세기(0 = 없음) */
  private readonly capePattern = uniform(0);
  /** 세트별 문양 종류: 0 없음, 1 이끼 잎맥, 2 잿불 불꽃, 3 서리 눈꽃, 4 늪 물결, 5 사막 모래+태양, 6 심연 눈/룬 */
  private readonly capePatternKind = uniform(0);
  private readonly capeMesh: THREE.Mesh;
  /** 천 시뮬(망토를 장착했고 본을 찾았을 때만). 망토 메시 정점을 매 프레임 덮어쓴다 */
  private cloth: CapeCloth | null = null;
  private clothBones: THREE.Object3D[] = [];
  private readonly capeTime = uniform(0);
  /** 이동 속도 0..1(느리게 따라간다) */
  private readonly capeSpeed = uniform(0);
  private gx = 0; private gy = 0; private gz = 0; private gInit = false;
  private readonly headMat: THREE.MeshBasicNodeMaterial;
  private readonly auraOuter: THREE.MeshBasicNodeMaterial;
  private readonly auraInner: THREE.MeshBasicNodeMaterial;
  private readonly outerRing: THREE.Mesh;
  private readonly innerRing: THREE.Mesh;
  private readonly tmp = new THREE.Vector3();
  private readonly dyeColor = new THREE.Color();
  private kRev = -1; private kHide = false; private kHero = ''; private kModel: KaelModel | null = null; private kMesh: THREE.Object3D | null = null; private kMat: unknown = null;
  private dyedMat: THREE.Material | null = null;
  /** 마스크 비동기 로드 사이에 다시 빌드·재질 교체가 일어났는지 가리는 번호 */
  private dyeSerial = 0;
  /** 실제로 그려지는 몸(라이라 몸을 읽는 중·실패면 라이라여도 카엘 몸) — 염색 마스크는 이 몸의 UV를 따른다 */
  private bodyId = '';
  private spin = 0;

  constructor() {
    this.headMat = new THREE.MeshBasicNodeMaterial({ color: 0xffffff, toneMapped: false });
    // 서클릿: 머리 둘레 얇은 고리(본 공간 단위 — attach 때 본 배율로 보정)
    this.headRing = new THREE.Mesh(new THREE.TorusGeometry(0.135, 0.014, 6, 28), this.headMat);
    this.headRing.rotation.x = Math.PI / 2;
    this.headRing.name = 'avatarHeadRing';

    this.capeMat = this.makeCapeMaterial();
    // 망토: 어깨 칼라에서 아래로 넓어지는 휜 천(원통 일부) + 물결 아랫단. 세로 9마디(정점 130개), 한 메시. 형상은 capeGeo.ts, 움직임은 capeCloth.ts(본 고정 천 시뮬 + 몸 충돌)
    const capeGeom = buildCapeGeometry();
    // 천 시뮬이 매 프레임 정점·노멀을 덮어쓴다
    (capeGeom.getAttribute('position') as THREE.BufferAttribute).setUsage(THREE.DynamicDrawUsage);
    (capeGeom.getAttribute('normal') as THREE.BufferAttribute).setUsage(THREE.DynamicDrawUsage);
    const capeMesh = new THREE.Mesh(capeGeom, this.capeMat);
    this.capeMesh = capeMesh;
    capeMesh.name = 'avatarCapeMesh';
    capeMesh.frustumCulled = false; // 천이 휴지 경계 밖으로 나갈 수 있다
    this.cape = new THREE.Group();
    this.cape.name = 'avatarCape';
    this.cape.add(capeMesh);

    const add = { transparent: true, depthWrite: false, blending: THREE.AdditiveBlending, toneMapped: false, side: THREE.DoubleSide } as const;
    this.auraOuter = new THREE.MeshBasicNodeMaterial({ color: 0xffffff, opacity: 0.75, ...add });
    this.auraInner = new THREE.MeshBasicNodeMaterial({ color: 0xffffff, opacity: 0.45, ...add });
    this.outerRing = new THREE.Mesh(new THREE.RingGeometry(0.78, 0.9, 48, 1, 0, Math.PI * 1.7), this.auraOuter);
    this.innerRing = new THREE.Mesh(new THREE.RingGeometry(0.5, 0.56, 6, 1), this.auraInner);
    for (const r of [this.outerRing, this.innerRing]) { r.rotation.x = -Math.PI / 2; r.renderOrder = 2; }
    this.aura.add(this.outerRing, this.innerRing);
    this.aura.name = 'avatarAura';
    this.aura.visible = false;
  }

  /**
   * 망토 셀 재질(캐릭터와 같은 MeshToon + 어두운 외곽선 띠). 색은 uv 기반 TSL로 계산한다 — 텍스처 없음.
   * 위 밝게·아래 어둡게 세로 그라데이션 + 세로 주름, 가장자리 트림(accent), 레어 문양 띠, 안쪽 면은 더 어둡게(양면).
   * 움직임은 CPU 천 시뮬이 정점을 직접 쓴다. 셰이더 정점 변위는 약한 잔물결만(뒤로만).
   */
  private makeCapeMaterial(): THREE.MeshToonNodeMaterial {
    const m = new THREE.MeshToonNodeMaterial({ color: 0xffffff, side: THREE.DoubleSide });
    const hw = CAPE_W_BOTTOM * 1.6, H = CAPE_H;
    m.colorNode = Fn(() => {
      const t = uv(), u = t.x, v = t.y;
      const fold = sin(u.mul(CAPE_COLS * Math.PI / 2).add(v.mul(1.3))).mul(0.5).add(0.5);
      const shade = mix(float(1.12), float(0.6), v.pow(0.9)).mul(float(0.9).add(fold.mul(0.14)));
      // 가장자리까지의 거리(m): 좌우·아랫단
      const d = min(min(u, float(1).sub(u)).mul(hw), float(1).sub(v).mul(H));
      const trimMask = float(1).sub(smoothstep(this.capeTrimW.sub(0.004), this.capeTrimW, d));
      const base = this.capeMain.mul(shade);
      let col = mix(base, this.capeAccent.mul(shade.mul(0.35).add(0.75)), trimMask);
      // 레어 문양 띠: 트림 안쪽의 점선 + 마름모
      const dd = d.sub(this.capeTrimW);
      const stitch = step(abs(dd.sub(0.022)), 0.0035);
      const dia = step(abs(fract(u.mul(hw).div(0.07)).sub(0.5)).mul(0.07).add(abs(dd.sub(0.05))), 0.013);
      const pat = max(stitch, dia).mul(this.capePattern).mul(step(0.0, dd));
      col = mix(col, this.capeAccent.mul(1.1), pat);
      // 세트별 문양(절차 무늬, 텍스처 없음): 트림 안쪽에서만 그린다. px/py = 망토 로컬 m(가운데 0, 위에서 아래로)
      const wHalf = mix(float(CAPE_W_TOP), float(CAPE_W_BOTTOM), v.pow(0.85));
      const px = u.sub(0.5).mul(wHalf).mul(2), py = v.mul(H);
      const inner = step(0.0, dd.sub(0.014)).mul(step(0.05, v));
      const PI = Math.PI;
      // 1 이끼 순례자: 가운데 줄기 + 갈래 잎맥(V자) + 양옆 덩굴
      const ax = abs(px);
      const vein = step(abs(fract(py.mul(1 / 0.075).sub(ax.mul(10))).sub(0.5)), 0.08).mul(step(ax, 0.12)).mul(step(0.012, ax)).mul(step(0.08, py)).mul(step(py, 0.46));
      const stem = step(ax, 0.007).mul(step(0.07, py)).mul(step(py, 0.5));
      const vine = step(abs(ax.sub(0.145).sub(sin(py.mul(16)).mul(0.012))), 0.0055).mul(step(0.1, py)).mul(step(py, 0.5));
      const p1 = max(max(vein, stem), vine);
      // 2 잿불 방랑자: 아랫단에서 올라오는 불꽃 톱니(바깥 불꽃 + 안쪽 밝은 심지)
      const hm = float(1).sub(v).mul(H);
      const tooth = abs(fract(px.mul(1 / 0.075).add(0.5)).sub(0.5)).mul(2);
      const fh = float(0.05).add(tooth.pow(1.4).mul(0.17));
      const p2 = max(step(hm, fh).mul(0.8), step(hm, fh.mul(0.5)).mul(1.0));
      // 3 서리 기사단: 여섯 갈래 눈꽃 결정(가지 + 갈래 V + 중앙 육각 고리)
      const sy = py.sub(0.26), sr = length(vec2(px, sy)), sa = atan(sy, px);
      const sf = abs(fract(sa.mul(3 / PI).add(0.5)).sub(0.5)).mul(PI / 3);
      const sd = sr.mul(sin(sf)), sal = sr.mul(sf.cos());
      const arm = step(sd, 0.0075).mul(step(sr, 0.115));
      const br1 = step(abs(sd.sub(float(0.105).sub(sal).mul(0.7))), 0.006).mul(step(0.07, sal)).mul(step(sal, 0.105));
      const br2 = step(abs(sd.sub(float(0.07).sub(sal).mul(0.7))), 0.006).mul(step(0.04, sal)).mul(step(sal, 0.07));
      const hexr = step(abs(sr.mul(float(PI / 6).sub(sf).cos()).sub(0.026)), 0.0045);
      const p3 = max(max(arm, br1), max(br2, hexr));
      // 4 늪의 은자: 물결 동심원 두 곳
      const rip = (cx: number, cy: number, rad: number) => { const r = length(vec2(px.sub(cx), py.sub(cy))); return step(abs(fract(r.mul(1 / 0.036)).sub(0.5)), 0.14).mul(step(r, rad)); };
      const p4 = max(rip(-0.04, 0.2, 0.115), rip(0.055, 0.38, 0.085));
      // 5 사막 순례: 아래쪽 모래 물결 줄무늬 + 위쪽 태양 원(고리·광선)
      const stripe = step(abs(fract(py.add(sin(px.mul(26)).mul(0.011)).mul(1 / 0.05)).sub(0.5)), 0.13).mul(step(0.3, py)).mul(step(py, 0.54));
      const dy = py.sub(0.14), dr = length(vec2(px, dy)), da = atan(dy, px);
      const sun = max(step(dr, 0.042), step(abs(dr.sub(0.064)), 0.0045));
      const ray = step(abs(fract(da.mul(4 / PI)).sub(0.5)), 0.2).mul(step(0.078, dr)).mul(step(dr, 0.105));
      const p5 = max(max(stripe, sun), ray);
      // 6 심연의 망령: 가운데 눈(아몬드 + 눈동자) + 점선 룬 고리
      const ex = abs(px).mul(1 / 0.1), lid = max(float(1).sub(ex.mul(ex)), 0.0).mul(0.062);
      const eyeOut = step(abs(abs(py.sub(0.26)).sub(lid)), 0.0055).mul(step(ex, 1.0));
      const er = length(vec2(px, py.sub(0.26))), ea = atan(py.sub(0.26), px);
      const pupil = step(er, 0.024);
      const runeRing = step(abs(er.sub(0.12)), 0.0045).mul(step(0.4, fract(ea.mul(4 / PI))));
      const p6 = max(max(eyeOut, pupil), runeRing);
      const kind = this.capePatternKind;
      const motif = select(kind.equal(1), p1, select(kind.equal(2), p2, select(kind.equal(3), p3, select(kind.equal(4), p4, select(kind.equal(5), p5, select(kind.equal(6), p6, float(0)))))));
      col = mix(col, this.capeAccent.mul(float(1.05).add(motif.mul(0.1))), motif.mul(inner).mul(0.92));
      // 외곽선(좌우·아랫단)
      const outline = float(1).sub(smoothstep(0.006, 0.011, d));
      col = mix(col, vec3(0.043, 0.059, 0.11), outline);
      return select(frontFacing, col, col.mul(0.5));
    })();
    // 주 움직임은 천 시뮬(capeCloth.ts). 셰이더는 아주 약한 잔물결만 더한다 — 뒤(-Z)로만 밀어 몸 쪽으로 파고들지 않는다
    m.positionNode = Fn(() => {
      const t = uv(), u = t.x, v = t.y, w = v.mul(v);
      const wave = sin(this.capeTime.mul(this.capeSpeed.mul(5).add(1.6)).sub(v.mul(5)).add(u.mul(3))).mul(0.5).add(0.5);
      return positionLocal.add(vec3(0, 0, wave.mul(float(0.004).add(this.capeSpeed.mul(0.006))).mul(w).negate()));
    })();
    return m;
  }

  /**
   * 프레임마다 부른다. ground = 영웅 발밑 위치(HeroVisual.groundPosition). dt = 실시간 초.
   * 바뀐 것이 없으면 오라 위치·회전만 갱신한다.
   */
  update(view: AvatarView | undefined, heroId: string, model: KaelModel | null, ground: THREE.Vector3, dt: number, bodyId: string = heroId): void {
    this.bodyId = bodyId;
    const rev = view ? view.revision : -1, hide = view ? view.hide : true;
    if (rev !== this.kRev || hide !== this.kHide || heroId !== this.kHero || model !== this.kModel || (model && (model.mesh !== this.kMesh || model.mesh.material !== this.kMat))) {
      this.kRev = rev; this.kHide = hide; this.kHero = heroId; this.kModel = model; this.kMesh = model ? model.mesh : null; this.kMat = model ? model.mesh.material : null;
      this.rebuild(view, heroId, model);
    }
    if (this.cape.parent) { this.flutter(ground, dt); this.simulateCape(dt); }
    if (this.aura.visible) {
      this.spin += dt * 0.9;
      this.aura.position.set(ground.x, ground.y + 0.03, ground.z);
      this.outerRing.rotation.z = this.spin;
      this.innerRing.rotation.z = -this.spin * 1.6;
    }
  }

  /** 천 시뮬 한 프레임: 본·망토 그룹의 월드 행렬을 최신으로 맞춘 뒤 시뮬하고 정점을 덮어쓴다. 객체 생성 없음. */
  private simulateCape(dt: number): void {
    const cl = this.cloth;
    if (!cl) return;
    this.cape.updateWorldMatrix(true, false);
    const bs = this.clothBones;
    for (let i = 0; i < bs.length; i++) bs[i].updateWorldMatrix(true, false);
    cl.update(dt);
    cl.applyTo(this.capeMesh.geometry);
  }

  /** 영웅 이동 속도(발밑 위치 변화)로 펄럭임 세기를 갱신한다. 객체 생성 없음. */
  private flutter(ground: THREE.Vector3, dt: number): void {
    this.capeTime.value += Math.min(dt, 0.1);
    if (!this.gInit) { this.gx = ground.x; this.gy = ground.y; this.gz = ground.z; this.gInit = true; return; }
    const dx = ground.x - this.gx, dz = ground.z - this.gz;
    this.gx = ground.x; this.gy = ground.y; this.gz = ground.z;
    const v = dt > 1e-4 ? Math.min(Math.hypot(dx, dz) / dt, 12) : 0; // 순간이동·씬 전환은 12m/s로 자른다
    const target = Math.min(v / 7, 1); // 달리기 7m/s = 1
    const k = 1 - Math.exp(-dt * (target > this.capeSpeed.value ? 9 : 4));
    this.capeSpeed.value += (target - this.capeSpeed.value) * k;
  }

  private equippedId(view: AvatarView | undefined, i: number): string | null {
    return view && !view.hide ? view.equipped[i] ?? null : null;
  }

  private rebuild(view: AvatarView | undefined, heroId: string, model: KaelModel | null): void {
    this.restoreDye();
    this.headRing.removeFromParent();
    this.cape.removeFromParent();
    this.cloth = null; this.clothBones.length = 0;
    const head = avatarDef(this.equippedId(view, SLOT_INDEX.head));
    const dye = avatarDef(this.equippedId(view, SLOT_INDEX.dye));
    const cape = avatarDef(this.equippedId(view, SLOT_INDEX.cape));
    const weapon = avatarDef(this.equippedId(view, SLOT_INDEX.weapon));
    const aura = avatarDef(this.equippedId(view, SLOT_INDEX.aura));

    this.aura.visible = !!aura;
    if (aura) {
      this.auraOuter.color.setHex(aura.color);
      this.auraInner.color.setHex(aura.accent);
      // 레어 = 진하게, 상급 = 옅게
      this.auraOuter.opacity = aura.grade === 'rare' ? 0.85 : 0.6;
    }
    if (!model) return;
    const skel = (model.mesh as THREE.SkinnedMesh).skeleton;
    if (head) {
      const bone = findBone(skel, HEAD_BONE);
      if (bone) {
        this.headMat.color.setHex(head.color);
        this.attach(bone, this.headRing, 0.16, 0.02);
      }
    }
    if (cape) {
      const bone = findBone(skel, CAPE_BONE);
      if (bone) {
        this.capeMain.value.setHex(cape.color);
        this.capeAccent.value.setHex(cape.accent);
        // 세트(레어) 망토는 가장자리 문양 띠가 더 굵다, 상급·일반은 단순
        const rare = cape.grade === 'rare';
        this.capeTrimW.value = rare ? 0.075 : cape.grade === 'fine' ? 0.05 : 0.04;
        this.capePattern.value = rare ? 1 : 0;
        const setNo = rare ? /^av_r([1-6])_cape$/.exec(cape.id) : null;
        this.capePatternKind.value = setNo ? Number(setNo[1]) : 0;
        this.gInit = false;
        this.attach(bone, this.cape, 0.12, -0.13);
        // 천 시뮬: 몸 충돌용 본을 찾아 붙인다(못 찾은 본의 캡슐은 생략)
        const bones: ClothBodyBones = { hips: findBone(skel, HIPS_BONE), spine2: bone };
        for (const k of Object.keys(LEG_BONES) as (keyof typeof LEG_BONES)[]) bones[k] = findBone(skel, LEG_BONES[k]);
        for (const o of Object.values(bones)) if (o) this.clothBones.push(o);
        this.cloth = new CapeCloth(capeShape().position, this.cape, bones, CAPE_MODEL_SCALE);
      }
    }
    if (dye) this.applyDye(model, dye.color, this.bodyId);
    // 무기 외형은 카엘 대검에만(라이라는 활 단계 빛을 쓴다 — PRD 1.1)
    if (heroId === 'kael' && weapon) {
      const c = rgb(weapon.color), a = rgb(weapon.accent);
      model.setWeaponLookOverride({ edge: [a[0] * 1.5, a[1] * 1.5, a[2] * 1.5], glow: [c[0] * 0.4, c[1] * 0.4, c[2] * 0.4], blade: [c[0] * 0.55, c[1] * 0.55, c[2] * 0.55], tint: [0.55 + c[0] * 0.45, 0.55 + c[1] * 0.45, 0.55 + c[2] * 0.45] });
    } else model.setWeaponLookOverride(null);
  }

  /** 본 자식으로 붙이고 본의 월드 배율을 상쇄해 월드 단위(m)로 맞춘다. up = 본 +Y 방향 거리, back = 본 +Z(앞) 방향 거리(음수 = 등 쪽) */
  private attach(bone: THREE.Bone, obj: THREE.Object3D, up: number, back: number): void {
    bone.updateWorldMatrix(true, false);
    bone.getWorldScale(this.tmp);
    const s = this.tmp.x > 1e-6 ? 1 / this.tmp.x : 1;
    // 모델 배율(KAEL_SCALE)까지 상쇄하면 다른 키의 영웅에서도 같은 크기다. 1.5배 모델 기준 크기를 원하므로 모델 배율만큼은 남긴다
    const model = CAPE_MODEL_SCALE;
    obj.scale.setScalar(s * model);
    obj.position.set(0, up * s, back * s);
    bone.add(obj);
  }

  /**
   * 몸 염색 2차(PRD 5절): 부위 마스크(옷=1, 피부·머리·손·밝은 금속=0)가 있으면 그 부분만 진하게 물들인다.
   * 마스크를 읽는 동안은 예전 방식(전체에 약하게 곱함)으로 바로 칠하고, 읽히면 교체한다. 마스크가 없는 몸은 예전 방식 그대로.
   */
  private applyDye(model: KaelModel, hex: number, bodyId: string): void {
    const mat = model.mesh.material;
    if (!mat || Array.isArray(mat)) return;
    this.dyedMat = mat;
    this.dyeColor.setHex(hex);
    const serial = ++this.dyeSerial;
    // 라이라가 카엘 몸을 빌려 쓰는 동안(몸 로드 전·실패)은 라이라 색조가 같은 재질의 colorNode를 쓴다 — 마스크 노드로 겹치지 않고 color만 곱하는 예전 방식으로 둔다
    const id = bodyId === this.kHero ? dyeMaskId(bodyId, model.tierState.tier) : null;
    applyDyeMasked(mat, null, this.dyeColor, DYE_STRENGTH_FALLBACK);
    if (!id) return;
    void loadDyeMask(id).then((mask) => {
      if (!mask || serial !== this.dyeSerial || this.dyedMat !== mat) return; // 그사이 다시 빌드·재질 교체됨
      clearDye(mat);
      applyDyeMasked(mat, mask, this.dyeColor, DYE_STRENGTH_MASKED);
    });
  }
  /** 몸 재질을 다른 외형(라이라 색조)이 바꾸기 직전에 부른다: 염색을 걷고 다음 update에서 다시 빌드하게 한다 */
  resetDye(): void {
    if (!this.dyedMat) return;
    this.restoreDye();
    this.kRev = -2;
  }
  private restoreDye(): void {
    this.dyeSerial++;
    if (this.dyedMat) clearDye(this.dyedMat);
    this.dyedMat = null;
  }

  dispose(): void {
    this.restoreDye();
    this.headRing.removeFromParent(); this.cape.removeFromParent(); this.aura.removeFromParent();
    this.cloth = null; this.clothBones.length = 0;
    this.headRing.geometry.dispose();
    for (const c of this.cape.children) (c as THREE.Mesh).geometry.dispose();
    this.outerRing.geometry.dispose(); this.innerRing.geometry.dispose();
    for (const m of [this.headMat, this.capeMat, this.auraOuter, this.auraInner]) m.dispose();
  }
}
